/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import {isNotEmpty, sanitizeString} from "../../../../../../../Apis/functions";
import {FILE_MODEL} from "../../../../../Visitor/Visitor/Model/Model";

/**
 * Values of the vehicle the transport block of the movement is written with.
 */
export const GATE_TRANSPORT_FIELDS = ['companyOrArea', 'plateNo', 'vehicleModel', 'driversName', 'licenseNo'];

/**
 * Name the service reads each value of the vehicle with, for the ones the log
 * names differently.
 */
export const GATE_TRANSPORT_FIELD_NAMES = {
    driversName: 'driverName'
};

/**
 * Values the guard registers over a movement of a vehicle.
 *
 * The vehicle travels with the folio, so the log opens with what the requester
 * left and the guard only completes what is read at the gate.
 */
export const GATE_LOG_MODEL = {
    companyOrArea: '',
    plateNo: '',
    vehicleModel: '',
    driversName: '',
    licenseNo: '',
    entryDate: null,
    exitDate: null,
    notes: '',
    dispatchNote: '',
    movementNotes: '',
    /**
     * Values the log opens with for a folio.
     *
     * The vehicle travels with the folio, so it is read from it. A movement the
     * gate still has to write opens with no notes and no evidence, and one
     * already written is read with the ones it was registered with.
     *
     * @param {Object} params
     * @param {Object} params.item - Folio the movement belongs to.
     * @param {string} [params.dateName] - Value of the log the moment of the movement is written to.
     * @param {number|null} params.dateValue - Moment of the movement the log opens with.
     * @param {boolean} params.startsGateLog - Whether the gate writes the movement from scratch.
     * @param {Object|null} params.lastGateEvent - Last log the gate registered over the folio.
     * @param {Object|null} params.dispatchEvent - Log the dispatch of the movement was closed with.
     *
     * @returns {Object} Values of the log.
     */
    set({item, dateName, dateValue, startsGateLog, lastGateEvent, dispatchEvent}) {
        return {
            ...Object.fromEntries(GATE_TRANSPORT_FIELDS.map((name) => [name, item?.[name] || ''])),
            entryDate: dateName === 'entryDate' ? dateValue : null,
            exitDate: dateName === 'exitDate' ? dateValue : null,
            notes: startsGateLog ? '' : sanitizeString(lastGateEvent?.notes) || '',
            dispatchNote: startsGateLog ? '' : sanitizeString(dispatchEvent?.notes) || '',
            evidence: startsGateLog
                ? []
                : (lastGateEvent?.evidence || [])
                    .filter((e) => e?.fileUploadDto)
                    .map((e) => FILE_MODEL.set(e))
        };
    },
    /**
     * Transport of the movement as the gate completes it.
     *
     * A value the requester registered travels with the folio and is sent back
     * as it was, and only the one it left blank is written by the guard, the
     * same way every form of material control is completed at the gate. The
     * update is not sent when the guard wrote nothing.
     *
     * @param {Object} item - Folio the movement belongs to.
     * @param {string} userId - User that registers the values.
     *
     * @returns {Object|null} Body of the update, or null when the gate wrote nothing.
     */
    toRegisterTransport(item, userId) {
        const transport = {};
        let wroteValue = false;

        GATE_TRANSPORT_FIELDS.forEach((name) => {
            const registered = isNotEmpty(item?.[name]);
            const value = registered ? item[name] : sanitizeString(this[name]);

            transport[GATE_TRANSPORT_FIELD_NAMES[name] || name] = value;

            if (!registered && value !== null)
                wroteValue = true;
        });

        if (!wroteValue)
            return null;

        return {
            id: item?.transport?.id,
            materialControlId: item?.id,
            ...transport,
            userId
        };
    },
    /**
     * Values of the movement of the folio as the gate completes them: its date
     * and its notes.
     *
     * Both are optional for the requester, so the ones it registered travel
     * back as they were and only the ones left blank are written by the guard.
     * The service writes every value of the movement with the update, so the
     * platforms and the details the folio already carries travel back too. The
     * update is not sent when the guard wrote nothing.
     *
     * @param {Object} item - Folio the movement belongs to.
     * @param {string} [dateName] - Value of the log the moment of the movement is read from.
     * @param {string} userId - User that registers the values.
     *
     * @returns {Object|null} Body of the update, or null when the gate wrote nothing.
     */
    toRegisterMovementValues(item, dateName, userId) {
        const date = dateName ? this[dateName] : null;
        const movementDate = date === null || date === undefined
            ? null
            : new Date(date).getTime();
        const writesMovementDate = !isNotEmpty(item?.expectedMovementDate)
            && movementDate !== null;
        const movementNotes = sanitizeString(this.movementNotes);
        const writesMovementNotes = !isNotEmpty(item?.movementNotes)
            && movementNotes !== null;

        if (!writesMovementDate && !writesMovementNotes)
            return null;

        return {
            id: item?.id,
            expectedMovementDate: writesMovementDate
                ? movementDate
                : (item?.expectedMovementDate ?? null),
            platform1No: item?.platform1No ?? null,
            platform2No: item?.platform2No ?? null,
            extraDetails: item?.extraDetails ?? null,
            movementNotes: writesMovementNotes
                ? movementNotes
                : (item?.movementNotes ?? null),
            userId
        };
    }
};
